import { Checkbox, CheckboxChangeEvent } from '@progress/kendo-react-inputs';
import { useInternationalization } from '@progress/kendo-react-intl';
import clsx from 'clsx';
import { isEmpty } from 'lodash';
import {
  SyntheticEvent,
  useCallback,
  type FC,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
} from 'react';
import { useTranslation } from 'react-i18next';
import AddToCartIcon from 'src/assets/icons/AddToCartIcon';
import NoItemIcon from 'src/assets/icons/NoItemIcon';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import { ProductImage } from 'src/components/ProductImage';
import Button from 'src/components/design-system/Button';
import NumberStepper from 'src/components/design-system/NumberStepper';
import PopUp from 'src/components/design-system/PopUp';
import useGuardedAsyncCallback from 'src/hooks/useGuardedAsyncCallback';
import useRedirect from 'src/hooks/useRedirect';
import ProductAttributeIcons from 'src/sections/catalog_products/ProductAttributeIcons';
import { stopPropagation } from 'src/utils';
import { useContextSelector } from 'use-context-selector';
import { SearchProductDetailsContext } from './SearchProductDetailsContext';
import { SearchProductFiltersContext } from './SearchProductFiltersContext';
import { SearchProductSelectionsContext } from './SearchProductSelectionsContext';
import { SearchResultProduct } from './searchProductData';

export interface SearchPictureViewCardProps {
  selected: boolean;
  onSelect: () => void;
  product: SearchResultProduct;
  onQuantityChange?: (qty: number) => void;
  quantity?: number;
}

const SearchPictureViewCard: FC<SearchPictureViewCardProps> = ({
  selected,
  onSelect,
  product,
  onQuantityChange,
  quantity: propQuantity,
}): ReactElement => {
  const { t } = useTranslation();
  const intl = useInternationalization();
  const { getUrl, redirect } = useRedirect();
  const { description, price, uom, itemNum, brandName, catalogName, catalogId } = product;

  const isSupplierUser = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.isSupplierUser
  );
  const handleViewProductDetails = useContextSelector(
    SearchProductDetailsContext,
    (state) => state.handleViewProductDetails
  );
  const isFetchingProductDetails = useContextSelector(
    SearchProductDetailsContext,
    (state) => state.isFetchingProductDetails
  );
  const loadingProductDetails = useContextSelector(
    SearchProductDetailsContext,
    (state) => state.loadingProductDetails
  );
  const handleSingleItemAddToCart = useContextSelector(
    SearchProductSelectionsContext,
    (context) => context.handleSingleItemAddToCart
  );
  const isUpdatingCart = useContextSelector(
    SearchProductSelectionsContext,
    (context) => context.isUpdatingCart
  );
  const setIsSwapItemModalOpen = useContextSelector(
    SearchProductDetailsContext,
    (context) => context.setIsSwapItemModalOpen
  );
  const setSwapItemData = useContextSelector(
    SearchProductDetailsContext,
    (context) => context.setSwapItemData
  );

  const quantity = propQuantity !== undefined ? propQuantity : 0;

  const getDisplayValue = (value: string | number | null | undefined): string => {
    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      return isEmpty(trimmedValue) ? '' : trimmedValue;
    }
    if (isEmpty(value)) {
      return '';
    }
    return String(value);
  };

  const handleCheckboxKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    if (e.key === 'Enter' || e.key === ' ') {
      onSelect();
    }
  };

  const cardId = `search-product-card-${product.productKey}`;
  const descriptionId = `${cardId}-description`;
  const productDetailsId = `${cardId}-details`;

  const [handleAddToCartClick, isAddToCartPending] = useGuardedAsyncCallback(
    async (e: MouseEvent<HTMLButtonElement>, item: SearchResultProduct) => {
      e.preventDefault();
      e.stopPropagation();
      await handleSingleItemAddToCart({
        productId: item.productId,
        catalogId: item.catalogId,
        categoryId: item.categoryId,
        quantity,
        isCompanySpecified: item.isFormulary || false,
      });
    }
  );
  const isCartActionPending = isUpdatingCart || isAddToCartPending;
  const isDisabled = loadingProductDetails || isFetchingProductDetails || isCartActionPending;

  const isLb = uom?.toLowerCase() === 'lb';
  const formattedPrice = intl.format(isLb ? '{0:c3}' : '{0:c2}', Number(price));
  const displayDescription =
    description.length > 60 ? `${description.slice(0, 60)}...` : description;

  const handleOpenCatalog = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      stopPropagation(event);

      if (catalogId == null) {
        return;
      }

      redirect(
        getUrl('viewProductList')(
          catalogId.toString(),
          product.offlineSupplierId != null ? null : product.categoryId ?? null,
          product.offlineSupplierId ?? null
        ),
        false,
        true
      );
    },
    [catalogId, product.offlineSupplierId, product.categoryId, getUrl, redirect]
  );

  const handleSwapItemClick = (
    event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>
  ) => {
    event.stopPropagation();
    event.preventDefault();
    setIsSwapItemModalOpen(true);
    setSwapItemData({
      pageType: 3,
      supplierId: product.supplierId,
      supplierCompanyId: product.supplierCompanyId,
      itemNumber: product.itemNum,
      productId: product.productId,
      catalogId: product.catalogId,
      categoryId: product.categoryId,
      quantity,
      price: Number(product.price),
      uom: product.uom,
    });
  };

  return (
    <article
      onClick={(e) => handleViewProductDetails(e, product)}
      id={cardId}
      role="button"
      aria-label={t(
        'Product card: {{productName}}. {{status}}. Press Enter or Space to {{action}}.',
        {
          productName: description,
          status: selected ? t('Selected') : t('Not selected'),
          action: selected ? t('deselect') : t('select'),
        }
      )}
      aria-pressed={selected}
      aria-describedby={`${descriptionId} ${productDetailsId}`}
      className={clsx(
        'cursor-pointer transition-all duration-200',
        'hover:bg-[#ECF7FC] hover:text-white hover:border-[#3FA9DB]',
        'focus:outline-none',
        'relative overflow-hidden rounded-md border-[1px] border-[#C6C6C6] bg-white shadow-sm',
        'w-full max-w-[271.5px] h-[360px]',
        'sm:max-w-[271.5px] md:max-w-[290px]',
        selected && 'bg-[#ECF7FC] border-[#3FA9DB] shadow-none',
        'flex flex-col justify-between',
        isDisabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      <Checkbox
        id={cardId}
        className="absolute top-2 left-2.5 z-30 w-4 h-4 cursor-pointer focus:outline-none"
        checked={selected}
        disabled={isCartActionPending}
        aria-label={t('Select {{productName}}', { productName: description })}
        onChange={(_e: CheckboxChangeEvent): void => {
          onSelect();
        }}
        onKeyDown={handleCheckboxKeyDown}
        onClick={(e: MouseEvent<HTMLInputElement>): void => {
          e.stopPropagation();
        }}
      />

      <div className="w-[100%]">
        <div className="relative">
          <div
            style={{
              background:
                'linear-gradient(315deg, rgba(0, 0, 0, 0) 30%, rgba(0, 0, 0, 0.2) 60%, rgba(0, 0, 0, 0.5) 80%, rgb(0, 0, 0) 100%)',
              opacity: 0.3,
            }}
            className="absolute top-0 left-0 w-full h-full"
          />
          <ProductImage
            productKey={product.productKey}
            productId={product.productId}
            supplierId={product.supplierId}
            linkUrl={product.linkUrl}
            altText={product.altText}
            type="summary"
            imageHeight="100px"
            imageWidth="100%"
            imageClassName="mb-3 h-[90px] w-full object-cover sm:h-[100px] md:h-[120px] bg-gray-200"
          />
        </div>
        <p
          id={descriptionId}
          className={clsx(
            'text-sm font-medium sm:text-base px-3 break-all',
            'line-clamp-2',
            'text-[#111111]',
            'h-[48px]',
            'mb-1'
          )}
          data-tooltip-id="global-tooltip"
          data-tooltip-content={getDisplayValue(description)}
        >
          {getDisplayValue(displayDescription)}
        </p>
        <div className="flex w-full flex-col px-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center min-w-0 flex-1 pr-2">
              <span className={clsx('text-sm font-semibold sm:text-base', 'text-[#3F6578]')}>
                {formattedPrice}
              </span>
              {!isEmpty(uom) && (
                <span
                  className={clsx(
                    'text-xs font-normal sm:text-sm',
                    'text-[#AAAAAA]',
                    'inline-block max-w-full truncate'
                  )}
                  data-tooltip-id="global-tooltip"
                  data-tooltip-content={getDisplayValue(uom)}
                >
                  {' '}
                  / {uom}
                </span>
              )}
            </div>
            <ProductAttributeIcons
              attributes={product.attributes.filter(
                (attribute: string): boolean => attribute !== 'isImageAvailable'
              )}
            />
          </div>
        </div>
      </div>
      <div className="px-3 pb-3 sm:px-4">
        <dl id={productDetailsId} className="mb-3 grid grid-rows-3 gap-1.5 text-xs sm:text-sm">
          <div
            className="h-full w-full gap-1 grid grid-cols-[50px_1fr]"
            data-tooltip-id="global-tooltip"
            data-tooltip-content={getDisplayValue(itemNum)}
          >
            <dt
              className="font-normal text-[#AAAAAA] whitespace-nowrap truncate min-w-0"
              title={t('Item #')}
            >
              {t('Item #')}
            </dt>
            <dd className={clsx('font-normal', 'text-black', 'truncate w-full min-w-0')}>
              {getDisplayValue(itemNum)}
            </dd>
          </div>
          <div
            className="h-full w-full gap-1 grid grid-cols-[50px_1fr]"
            data-tooltip-id="global-tooltip"
            data-tooltip-content={getDisplayValue(brandName)}
          >
            <dt className="font-normal text-[#AAAAAA] whitespace-nowrap truncate min-w-0">
              {t('Brand')}
            </dt>
            <dd className={clsx('font-normal', 'text-black', 'truncate w-full min-w-0')}>
              {getDisplayValue(brandName)}
            </dd>
          </div>
          <div
            className="h-full w-full gap-1 grid grid-cols-[50px_1fr]"
            data-tooltip-id="global-tooltip"
            data-tooltip-content={getDisplayValue(catalogName)}
          >
            <dt
              className="font-normal text-[#AAAAAA] whitespace-nowrap truncate min-w-0"
              title={t('Catalog')}
            >
              {t('Catalog')}
            </dt>
            <dd className={clsx('font-normal', 'truncate w-full min-w-0')}>
              {catalogName && catalogId != null ? (
                <button
                  type="button"
                  className="text-[#3FA9DB] hover:underline focus:outline-2 focus:outline-black rounded text-left truncate w-full"
                  onClick={handleOpenCatalog}
                >
                  {getDisplayValue(catalogName)}
                </button>
              ) : (
                <span className="text-black">{getDisplayValue(catalogName)}</span>
              )}
            </dd>
          </div>
        </dl>
        <div className="flex w-full min-w-0 flex-row flex-nowrap items-center gap-1.5">
          <span className="shrink-0 text-xs font-normal text-[#AAAAAA] sm:text-sm">{t('Qty')}</span>
          <NumberStepper
            value={quantity}
            onChange={(e: SyntheticEvent<Element, Event>, value: number): void => {
              e.stopPropagation();
              onQuantityChange?.(value);
            }}
            onClick={(e: MouseEvent<HTMLDivElement>): void => {
              e.stopPropagation();
            }}
            onMouseDown={(e: MouseEvent<HTMLDivElement>): void => {
              e.stopPropagation();
            }}
            disabled={isSupplierUser || isCartActionPending}
            className="h-[25px] min-w-[72px] flex-1"
          />
          {!isSupplierUser && (
            <PopUp
              popupAlign={{ horizontal: 'center', vertical: 'top' }}
              anchorAlign={{ horizontal: 'center', vertical: 'bottom' }}
              className="max-h-[180px]"
              content={
                <div className="m-1 max-w-[350px]">
                  <PopUpContentWrapper type="Information" contents={[t('Quantity cannot be 0')]} />
                </div>
              }
              show={quantity === 0}
            >
              <div className="shrink-0">
                <Button
                  color="success"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddToCartClick(e, product);
                  }}
                  size="sm"
                  disabled={quantity === 0 || isCartActionPending}
                  loading={isCartActionPending}
                  className="!m-0 h-[25px] w-[30px] min-w-[30px] justify-center px-0"
                >
                  <AddToCartIcon
                    height="16px"
                    width="16px"
                    className={clsx({ 'cursor-not-allowed': quantity === 0 })}
                  />
                </Button>
              </div>
            </PopUp>
          )}

          {product.isNoItem && !isSupplierUser && (
            <div
              data-tooltip-id="global-tooltip"
              data-tooltip-content={t('Check Contracted Alternatives')}
              onClick={handleSwapItemClick}
              className="flex shrink-0 cursor-pointer items-center"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  handleSwapItemClick(event);
                }
              }}
            >
              <NoItemIcon />
            </div>
          )}

          <button
            disabled={isDisabled}
            type="button"
            onClick={(e) => handleViewProductDetails(e, product)}
            className="flex w-fit shrink-0 flex-col items-start leading-[1.1] text-xs font-medium text-[#3FA9DB] hover:underline focus:outline-2 focus:outline-black rounded sm:text-sm"
            aria-label={t('View more information about {{productName}}', {
              productName: description,
            })}
          >
            <span>{t('View')}</span>
            <span>{t('Info')}</span>
          </button>
        </div>
      </div>
    </article>
  );
};

export default SearchPictureViewCard;
